import { useCallback, useState } from "react"

import type { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFileUpload } from "@/hooks/useFileUpload"

interface UseFileUploadProps {
  allowedFileTypes: string[]
  referenceTableName: ReferenceTableName
}

type UploadState = Record<string, boolean>

interface FileUploadHook {
  uploading: UploadState
  uploadFile: (file: File, type: FieldGroupEnum, referenceId: string, label?: string) => Promise<boolean>
}

export const useFileUploadWithValidation = ({
  allowedFileTypes,
  referenceTableName,
}: UseFileUploadProps): FileUploadHook => {
  const { uploadFile: baseUploadFile } = useFileUpload()
  const [uploading, setUploading] = useState<UploadState>({})

  const uploadFile = useCallback(
    async (file: File, type: FieldGroupEnum, referenceId: string, label?: string) => {
      if (!allowedFileTypes.includes(file.type)) {
        console.error(`Invalid file type: ${file.type}. Allowed types: ${allowedFileTypes.join(", ")}`)
        return false
      }

      if (!referenceId) {
        console.error("No reference ID available")
        return false
      }

      setUploading((prev) => ({ ...prev, [type]: true }))

      try {
        const success = await baseUploadFile({
          file,
          type,
          metadata: {
            fieldGroup: type,
            referenceId,
            referenceTableName,
          },
          label,
        })

        if (!success) {
          console.error("Failed to upload file")
          return false
        }

        return true
      } catch (error) {
        console.error("Failed to upload file:", error)
        return false
      } finally {
        setUploading((prev) => ({ ...prev, [type]: false }))
      }
    },
    [allowedFileTypes, baseUploadFile, referenceTableName]
  )

  return {
    uploading,
    uploadFile,
  }
}
